<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contributor-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="contributor-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.sectionsAriaLabel">
          <button
            v-for="section in resolvedSections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <ContributorSectionFiscalIdentification
              :active="activeSectionKey === 'fiscal-ident'"
              :form="form"
              :v$="v$"
              :contributor-type-options="contributorTypeOptions"
              :tax-identifier-label="taxIdentifierLabel"
              :regimen-options="normalizedRegimenOptions"
              :uso-cfdi-options="normalizedUsoCfdiOptions"
              :payment-method-options="normalizedPaymentMethodOptions"
              :payment-method-type-options="normalizedPaymentMethodTypeOptions"
              :is-national-type="isNationalType"
              @constancia-selected="onConstanciaSelected"
            />

            <ContributorSectionFiscalAddress
              :active="activeSectionKey === 'fiscal-address'"
              :form="form"
              :v$="v$"
              :state-options="normalizedStateOptions"
            />

            <ContributorSectionBankData
              :active="activeSectionKey === 'bank-data'"
              :form="form"
              :v$="v$"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode && showDeleteAction"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete contributor-modal__delete-btn"
                  :title="deleteActionLabel"
                  :aria-label="deleteActionLabel"
                  :disabled="loading"
                  @click="emitDelete"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" :disabled="loading" @click="emitClose">{{ text.cancel }}</button>
                <button type="submit" class="btn btn-primary" :disabled="loading">{{ submitButtonLabel }}</button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import {
  findOptionByRawValue,
  getOptionCode,
  getOptionId,
  getOptionValue,
} from '@/utils/optionValues';
import ContributorSectionFiscalIdentification from '@/components/contributors/modal/sections/ContributorSectionFiscalIdentification.vue';
import ContributorSectionFiscalAddress from '@/components/contributors/modal/sections/ContributorSectionFiscalAddress.vue';
import ContributorSectionBankData from '@/components/contributors/modal/sections/ContributorSectionBankData.vue';
import {
  CONTRIBUTOR_SECTIONS,
  FOREIGN_RFC_FALLBACK,
  createInitialContributorForm,
} from '@/composables/contributors/contributorModalConfig';

const { catalog: billingDataText } = useDomainText('billingData');

export default {
  name: 'ContributorModal',
  components: {
    ContributorSectionFiscalIdentification,
    ContributorSectionFiscalAddress,
    ContributorSectionBankData,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    stateOptions: {
      type: Array,
      default: () => [],
    },
    regimenOptions: {
      type: Array,
      default: () => [],
    },
    usoCfdiOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodTypeOptions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showDeleteAction: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit', 'delete'],
  data() {
    return {
      text: billingDataText.modal,
      sections: CONTRIBUTOR_SECTIONS,
      activeSectionKey: 'fiscal-ident',
      form: createInitialContributorForm(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? this.text.titleEdit : this.text.titleCreate;
    },
    modalSubtitle() {
      return this.isEditMode
        ? this.text.subtitleEdit
        : this.text.subtitleCreate;
    },
    submitButtonLabel() {
      return this.isEditMode ? this.text.submitEdit : this.text.submitCreate;
    },
    deleteActionLabel() {
      return billingDataText.list.actions.delete;
    },
    contributorTypeOptions() {
      return [
        { value: 0, label: this.text.contributorTypes.national },
        { value: 1, label: this.text.contributorTypes.foreign },
      ];
    },
    resolvedSections() {
      return this.sections.map((section) => {
        const sectionByKey = {
          'fiscal-ident': this.text.sections.fiscalIdent,
          'fiscal-address': this.text.sections.fiscalAddress,
          'bank-data': this.text.sections.bankData,
        };

        const sectionText = sectionByKey[section.key] || {};

        return {
          ...section,
          title: sectionText.title || section.key,
          description: sectionText.description || '',
        };
      });
    },
    typeValue() {
      const rawValue = getOptionValue(this.form?.type);
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    isNationalType() {
      return this.typeValue === 0;
    },
    taxIdentifierLabel() {
      return this.isNationalType
        ? this.text.taxIdentifierLabels.national
        : this.text.taxIdentifierLabels.foreign;
    },
    normalizedStateOptions() {
      return this.normalizeOptions(this.stateOptions);
    },
    normalizedRegimenOptions() {
      return this.normalizeOptions(this.regimenOptions);
    },
    normalizedUsoCfdiOptions() {
      return this.normalizeOptions(this.usoCfdiOptions);
    },
    normalizedPaymentMethodOptions() {
      return this.normalizeOptions(this.paymentMethodOptions);
    },
    normalizedPaymentMethodTypeOptions() {
      return this.normalizeOptions(this.paymentMethodTypeOptions);
    },
  },
  validations() {
    const requiredTrimmed = (value) => helpers.req(String(value || '').trim());
    const requiredOption = (value) => value !== null && value !== undefined;

    return {
      form: {
        name: {
          required: helpers.withMessage(
            this.text.validation.fiscalNameRequired,
            requiredTrimmed
          ),
        },
        zipcode: {
          required: helpers.withMessage(
            this.text.validation.zipCodeRequired,
            requiredTrimmed
          ),
        },
        taxIdentifier: {
          requiredNational: helpers.withMessage(
            this.text.validation.rfcRequiredNational,
            (value) => !this.isNationalType || requiredTrimmed(value)
          ),
        },
        regimen: {
          required: helpers.withMessage(
            this.text.validation.fiscalRegimeRequired,
            requiredOption
          ),
        },
        usoCfdi: {
          required: helpers.withMessage(
            this.text.validation.cfdiUseRequired,
            requiredOption
          ),
        },
        paymentMethod: {
          requiredNational: helpers.withMessage(
            this.text.validation.paymentMethodRequiredNational,
            (value) => !this.isNationalType || requiredOption(value)
          ),
        },
        paymentMethodType: {
          requiredNational: helpers.withMessage(
            this.text.validation.paymentMethodTypeRequiredNational,
            (value) => !this.isNationalType || requiredOption(value)
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.activeSectionKey = this.sections[0].key;
        this.form = this.buildFormFromInitialData();
        this.v$.$reset();
        return;
      }

      this.activeSectionKey = this.sections[0].key;
      this.form = createInitialContributorForm();
      this.v$.$reset();
    },
    initialData() {
      if (!this.show) {
        return;
      }

      this.form = this.buildFormFromInitialData();
      this.v$.$reset();
    },
  },
  methods: {
    normalizeOptions(options) {
      return normalizeCatalogOptions(options);
    },
    defaultOptionByPredicate(options, predicate) {
      const list = Array.isArray(options) ? options : [];
      return list.find(predicate) || null;
    },
    resolveDefaultRegimenOption() {
      const byCode = this.defaultOptionByPredicate(this.normalizedRegimenOptions, (option) => {
        return String(option?.meta?.code || '').trim() === '612';
      });

      return byCode || this.normalizedRegimenOptions[0] || null;
    },
    resolveDefaultUsoCfdiOption() {
      const byCode = this.defaultOptionByPredicate(this.normalizedUsoCfdiOptions, (option) => {
        return String(option?.meta?.code || '').trim().toUpperCase() === 'G03';
      });

      return byCode || this.normalizedUsoCfdiOptions[0] || null;
    },
    resolveDefaultPaymentMethodOption() {
      const byTransferCode = this.defaultOptionByPredicate(this.normalizedPaymentMethodOptions, (option) => {
        return String(option?.meta?.code || '').trim() === '03';
      });

      if (byTransferCode) {
        return byTransferCode;
      }

      const byTransferName = this.defaultOptionByPredicate(this.normalizedPaymentMethodOptions, (option) => {
        const name = String(option?.meta?.name || '').trim().toLowerCase();
        return name.includes('transferencia');
      });

      return byTransferName || this.normalizedPaymentMethodOptions[0] || null;
    },
    resolveDefaultPaymentMethodTypeOption() {
      const byCode = this.defaultOptionByPredicate(this.normalizedPaymentMethodTypeOptions, (option) => {
        return String(option?.meta?.code || '').trim().toUpperCase() === 'PUE';
      });

      return byCode || this.normalizedPaymentMethodTypeOptions[0] || null;
    },
    onConstanciaSelected(event) {
      const file = event?.target?.files?.[0] || null;
      this.form.constanciaFile = file;
      this.form.constanciaFileName = file ? String(file.name || '').trim() : '';
    },
    emitClose() {
      this.$emit('close');
    },
    emitDelete() {
      this.$emit('delete', this.initialData || null);
    },
    buildFormFromInitialData() {
      const initial = this.initialData || {};
      const initialType = Number(initial?.type ?? 0) === 1 ? 1 : 0;

      const regimen = findOptionByRawValue(this.normalizedRegimenOptions, [initial?.regimen_id, initial?.regimen]);
      const usoCfdi = findOptionByRawValue(this.normalizedUsoCfdiOptions, initial?.uso_cfdi);
      const paymentMethod = findOptionByRawValue(this.normalizedPaymentMethodOptions, initial?.payment_method);
      const paymentMethodType = findOptionByRawValue(this.normalizedPaymentMethodTypeOptions, initial?.payment_method_type);
      const state = findOptionByRawValue(this.normalizedStateOptions, [initial?.state_code, initial?.state]);

      const hasFiscalAddress = [
        initial?.state,
        initial?.municipality,
        initial?.street,
        initial?.exterior,
        initial?.interior,
        initial?.neighborhood,
      ].some((value) => String(value || '').trim() !== '');

      const baseForm = {
        ...createInitialContributorForm(),
        default: Number(initial?.default || 0) === 1 ? 1 : 0,
        type: findOptionByRawValue(this.contributorTypeOptions, initialType) || this.contributorTypeOptions[0],
        taxIdentifier: initialType === 1
          ? String(initial?.identifier || '').trim()
          : String(initial?.rfc || '').trim(),
        name: String(initial?.name || '').trim(),
        zipcode: String(initial?.zipcode || '').trim(),
        regimen: regimen || this.resolveDefaultRegimenOption(),
        usoCfdi: usoCfdi || this.resolveDefaultUsoCfdiOption(),
        paymentMethod: paymentMethod || this.resolveDefaultPaymentMethodOption(),
        paymentMethodType: paymentMethodType || this.resolveDefaultPaymentMethodTypeOption(),
        skipFiscalAddress: !hasFiscalAddress,
        state,
        municipality: String(initial?.municipality || '').trim(),
        street: String(initial?.street || '').trim(),
        exterior: String(initial?.exterior || '').trim(),
        interior: String(initial?.interior || '').trim(),
        neighborhood: String(initial?.neighborhood || '').trim(),
        paymentAccount: String(initial?.payment_account || '').trim(),
        beneficiaryBankRfc: String(initial?.rfc_banco_beneficiario || '').trim(),
        orderingBank: String(initial?.nom_banco_ord_ext || '').trim(),
        orderingBankRfc: String(initial?.rfc_emisor_cta_ord || '').trim(),
        orderingBankAccount: String(initial?.cta_ordenante || '').trim(),
      };

      if (initial && Object.keys(initial).length === 0) {
        baseForm.skipFiscalAddress = false;
      }

      return baseForm;
    },
    submitForm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        this.activeSectionKey = 'fiscal-ident';
        return;
      }

      const name = String(this.form.name || '').trim();
      const zipcode = String(this.form.zipcode || '').trim();
      const taxIdentifier = String(this.form.taxIdentifier || '').trim();

      const selectedRegimen = findOptionByRawValue(this.normalizedRegimenOptions, this.form.regimen);
      const selectedUsoCfdi = findOptionByRawValue(this.normalizedUsoCfdiOptions, this.form.usoCfdi);
      const selectedPaymentMethod = findOptionByRawValue(this.normalizedPaymentMethodOptions, this.form.paymentMethod);
      const selectedPaymentMethodType = findOptionByRawValue(this.normalizedPaymentMethodTypeOptions, this.form.paymentMethodType);
      const selectedState = findOptionByRawValue(this.normalizedStateOptions, this.form.state);

      const payload = {
        default: Number(this.form.default || 0) === 1 ? 1 : 0,
        type: this.isNationalType ? 0 : 1,
        rfc: this.isNationalType ? taxIdentifier : FOREIGN_RFC_FALLBACK,
        identifier: this.isNationalType ? '' : taxIdentifier,
        name,
        zipcode,
        regimen_id: getOptionId(selectedRegimen, { positive: true }),
        regimen: getOptionCode(selectedRegimen),
        uso_cfdi: getOptionCode(selectedUsoCfdi),
        payment_method: getOptionCode(selectedPaymentMethod),
        payment_method_type: getOptionCode(selectedPaymentMethodType),
        payment_account: String(this.form.paymentAccount || '').trim(),
        rfc_banco_beneficiario: String(this.form.beneficiaryBankRfc || '').trim(),
        nom_banco_ord_ext: String(this.form.orderingBank || '').trim(),
        rfc_emisor_cta_ord: String(this.form.orderingBankRfc || '').trim(),
        cta_ordenante: String(this.form.orderingBankAccount || '').trim(),
        state: this.form.skipFiscalAddress ? '' : getOptionCode(selectedState),
        municipality: this.form.skipFiscalAddress ? '' : String(this.form.municipality || '').trim(),
        street: this.form.skipFiscalAddress ? '' : String(this.form.street || '').trim(),
        exterior: this.form.skipFiscalAddress ? '' : String(this.form.exterior || '').trim(),
        interior: this.form.skipFiscalAddress ? '' : String(this.form.interior || '').trim(),
        neighborhood: this.form.skipFiscalAddress ? '' : String(this.form.neighborhood || '').trim(),
      };

      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.contributor-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
